<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import { useDarkMode } from '../composables/useDarkMode';

interface Props {
  modelValue: boolean;
  businessName: string;
  prefill?: { firstName: string; lastName: string } | null;
  showBack?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'claimed'): void;
  (e: 'back'): void;
  (e: 'close'): void;
}>();

const { isDark } = useDarkMode();

type Step = 'form' | 'sending' | 'success' | 'error';
const step = ref<Step>('form');
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const emailError = ref('');
const serverError = ref('');
const allowedDomains = ref<string[]>([]);
const isSubmitting = ref(false);

const dialogOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val),
});

// Reset state when dialog opens, apply prefill if available
watch(dialogOpen, (open) => {
  if (open) {
    step.value = 'form';
    firstName.value = props.prefill?.firstName ?? '';
    lastName.value = props.prefill?.lastName ?? '';
    email.value = '';
    emailError.value = '';
    serverError.value = '';
    allowedDomains.value = [];
    isSubmitting.value = false;
  }
});

// Derive an example corporate email for the current business.
// Prefer domains configured on the business; otherwise fall back to a
// best-effort slug derived from the business name.
const exampleDomain = computed(() => {
  if (allowedDomains.value.length > 0) {
    return allowedDomains.value[0];
  }
  const slug = (props.businessName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return slug ? `${slug}.com` : 'yourdealership.com';
});

const exampleEmail = computed(() => `you@${exampleDomain.value}`);

// True when the server error is specifically about the email domain
// (free email / not in authorized domain list). We only show the
// "why we require a corporate email" explanation in that case.
const isDomainError = computed(() => {
  const msg = serverError.value.toLowerCase();
  return msg.includes('dealership email') || msg.includes('authorized domain');
});

const isValidEmail = (val: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
};

const canSubmit = computed(() => {
  return email.value.trim() !== '' && firstName.value.trim() !== '' && lastName.value.trim() !== '' && !isSubmitting.value;
});

const handleEmailSubmit = async () => {
  emailError.value = '';
  serverError.value = '';

  if (!email.value.trim()) {
    emailError.value = 'Email is required';
    return;
  }

  if (!isValidEmail(email.value.trim())) {
    emailError.value = 'Please enter a valid email address';
    return;
  }

  if (!firstName.value.trim()) {
    emailError.value = 'First name is required';
    return;
  }

  if (!lastName.value.trim()) {
    emailError.value = 'Last name is required';
    return;
  }

  isSubmitting.value = true;
  step.value = 'sending';

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  try {
    await axios.post('/api/trial_registrations', {
      email: email.value.trim(),
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
    }, {
      headers: {
        'X-CSRF-Token': csrfToken || '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    step.value = 'success';
    emit('claimed');
  } catch (err: unknown) {
    // Extract the specific error message from the backend response.
    // The trial_registrations#create endpoint returns `{ errors: [...] }` (array),
    // while other trial endpoints return `{ error: '...' }` (string). Support both.
    let message = '';
    let status: number | undefined;
    allowedDomains.value = [];
    if (axios.isAxiosError(err)) {
      status = err.response?.status;
      const data = err.response?.data as {
        error?: string;
        errors?: string[];
        allowed_domains?: string[];
      } | undefined;
      if (Array.isArray(data?.errors) && data!.errors!.length > 0) {
        message = data!.errors!.join(' ');
      } else if (typeof data?.error === 'string' && data.error.length > 0) {
        message = data.error;
      }
      if (Array.isArray(data?.allowed_domains)) {
        allowedDomains.value = data!.allowed_domains!;
      }
    }

    if (status === 422 && message) {
      // Validation failure — keep the user on the form so they can correct the input.
      step.value = 'form';
      serverError.value = message;
    } else {
      // Unknown/server error — show the full error screen.
      step.value = 'error';
      serverError.value = message || 'Something went wrong. Please try again.';
    }
  } finally {
    isSubmitting.value = false;
  }
};

const handleResend = async () => {
  isSubmitting.value = true;
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  try {
    await axios.post('/api/trial_registrations', {
      email: email.value.trim(),
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
    }, {
      headers: {
        'X-CSRF-Token': csrfToken || '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  } catch {
    // Silently fail on resend
  } finally {
    isSubmitting.value = false;
  }
};

const handleGoogle = () => {
  window.location.href = '/api/trial_registrations/google_redirect';
};

const handleLinkedIn = () => {
  // Navigate to LinkedIn OAuth — backend handles the full redirect flow
  window.location.href = '/api/trial_registrations/linkedin_redirect';
};

const closeDialog = () => {
  dialogOpen.value = false;
  emit('close');
};

const goBack = () => {
  emit('back');
};

const retryForm = () => {
  step.value = 'form';
  serverError.value = '';
};
</script>

<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="520"
    :class="{ 'claim-dialog--dark': isDark }"
    scrim
  >
    <v-card class="claim-dialog" :class="{ 'claim-dialog--dark': isDark }" rounded="xl">
      <!-- Back button (when coming from LinkedIn mismatch) -->
      <v-btn
        v-if="showBack && (step === 'form' || step === 'sending')"
        icon
        variant="text"
        size="small"
        class="claim-dialog__back"
        @click="goBack"
      >
        <v-icon size="22">mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Close button -->
      <v-btn
        icon
        variant="text"
        size="small"
        class="claim-dialog__close"
        @click="closeDialog"
      >
        <v-icon size="22">mdi-close</v-icon>
      </v-btn>

      <!-- FORM STATE -->
      <div v-if="step === 'form' || step === 'sending'" class="claim-dialog__content">
        <!-- Icon -->
        <div class="claim-dialog__icon-wrap">
          <v-icon size="36" color="white">mdi-account-plus</v-icon>
        </div>

        <!-- Title -->
        <h2 class="claim-dialog__title">Claim Your VinAssessment Account</h2>

        <!-- Subtitle -->
        <p class="claim-dialog__subtitle">
          Verify your identity to get full access to
          <strong>{{ businessName }}</strong>'s dashboard.
        </p>

        <!-- OAuth buttons -->
        <div class="claim-dialog__oauth-buttons">
          <button class="claim-dialog__oauth-btn claim-dialog__oauth-btn--google" @click="handleGoogle">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span>Continue with Google</span>
          </button>

          <button class="claim-dialog__oauth-btn claim-dialog__oauth-btn--linkedin" @click="handleLinkedIn">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#0A66C2">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            <span>Continue with LinkedIn</span>
          </button>
        </div>

        <!-- Divider -->
        <div class="claim-dialog__divider">
          <span class="claim-dialog__divider-line" />
          <span class="claim-dialog__divider-text">or</span>
          <span class="claim-dialog__divider-line" />
        </div>

        <!-- Email form -->
        <div class="claim-dialog__form">
          <div class="claim-dialog__field">
            <label class="claim-dialog__label">First Name</label>
            <input
              v-model="firstName"
              type="text"
              class="claim-dialog__input"
              :class="{ 'claim-dialog__input--dark': isDark }"
              placeholder="John"
              :disabled="step === 'sending'"
            />
          </div>

          <div class="claim-dialog__field">
            <label class="claim-dialog__label">Last Name</label>
            <input
              v-model="lastName"
              type="text"
              class="claim-dialog__input"
              :class="{ 'claim-dialog__input--dark': isDark }"
              placeholder="Smith"
              :disabled="step === 'sending'"
            />
          </div>

          <div class="claim-dialog__field">
            <label class="claim-dialog__label">Email</label>
            <input
              v-model="email"
              type="email"
              class="claim-dialog__input"
              :class="{ 'claim-dialog__input--dark': isDark, 'claim-dialog__input--error': !!emailError }"
              placeholder="john@dealership.com"
              :disabled="step === 'sending'"
              @keydown.enter="handleEmailSubmit"
            />
            <span v-if="emailError" class="claim-dialog__field-error">{{ emailError }}</span>
            <span class="claim-dialog__hint">Use your dealership email address</span>
          </div>

          <!-- Server error -->
          <v-alert
            v-if="serverError"
            type="error"
            variant="tonal"
            density="compact"
            class="claim-dialog__alert"
          >
            <div class="claim-dialog__alert-title">{{ serverError }}</div>

            <template v-if="isDomainError">
              <p class="claim-dialog__alert-body">
                To protect your dealership's data, we need to verify that you're
                affiliated with <strong>{{ businessName || 'this business' }}</strong>.
                Please use a corporate email address from this dealership —
                for example,
                <code class="claim-dialog__alert-code">{{ exampleEmail }}</code>.
              </p>
              <p class="claim-dialog__alert-body">
                Don't have a corporate email? No problem —
                <a
                  href="https://vinassessment.com/contact_us"
                  target="_blank"
                  rel="noopener noreferrer"
                >contact us</a>
                and we'll authorize you manually.
              </p>
            </template>

            <div v-else class="claim-dialog__alert-contact">
              Or
              <a href="https://vinassessment.com/contact_us" target="_blank" rel="noopener noreferrer">
                contact us
              </a>
              if you need help.
            </div>
          </v-alert>

          <button
            class="claim-dialog__email-btn"
            :class="{ 'claim-dialog__email-btn--dark': isDark }"
            :disabled="!canSubmit"
            @click="handleEmailSubmit"
          >
            <span v-if="step === 'sending'" class="d-flex align-center justify-center" style="gap: 8px;">
              <v-progress-circular indeterminate size="18" width="2" color="currentColor" />
              Sending...
            </span>
            <span v-else class="d-flex align-center justify-center" style="gap: 6px;">
              Continue with Email
              <v-icon size="18">mdi-arrow-right</v-icon>
            </span>
          </button>
        </div>

        <!-- Login link -->
        <p class="claim-dialog__login">
          Already have an account?
          <a href="/users/sign_in" class="claim-dialog__login-link">Log in</a>
        </p>
      </div>

      <!-- SUCCESS STATE -->
      <div v-else-if="step === 'success'" class="claim-dialog__content">
        <div class="claim-dialog__success-icon">
          <v-icon size="44" color="white">mdi-check-circle</v-icon>
        </div>

        <h2 class="claim-dialog__title">Check Your Email!</h2>

        <p class="claim-dialog__subtitle">
          We sent a confirmation link to
          <br />
          <strong>{{ email }}</strong>
        </p>

        <p class="claim-dialog__subtitle">
          Click the link to activate your account.
        </p>

        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="claim-dialog__alert"
        >
          Didn't see it? Check your spam or junk folder.
        </v-alert>

        <div class="claim-dialog__success-actions">
          <button class="claim-dialog__close-btn" @click="closeDialog">Close</button>
          <button
            class="claim-dialog__resend-btn"
            :disabled="isSubmitting"
            @click="handleResend"
          >
            <v-progress-circular v-if="isSubmitting" indeterminate size="16" width="2" color="currentColor" />
            <span v-else>Resend Email</span>
          </button>
        </div>
      </div>

      <!-- ERROR STATE -->
      <div v-else-if="step === 'error'" class="claim-dialog__content">
        <div class="claim-dialog__error-icon">
          <v-icon size="44" color="white">mdi-alert-circle</v-icon>
        </div>

        <h2 class="claim-dialog__title">Something Went Wrong</h2>

        <p class="claim-dialog__subtitle">{{ serverError }}</p>

        <p class="claim-dialog__subtitle claim-dialog__contact-line">
          Or
          <a href="https://vinassessment.com/contact_us" target="_blank" rel="noopener noreferrer">
            contact us
          </a>
          if you need help.
        </p>

        <button class="claim-dialog__email-btn" @click="retryForm">
          Try Again
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.claim-dialog {
  position: relative;
  padding: 40px 36px 32px;
  background: white;
}

.claim-dialog--dark {
  background: #1e293b !important;
}

.claim-dialog__back {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
}

.claim-dialog__close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
}

.claim-dialog__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

/* Icon circle */
.claim-dialog__icon-wrap {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3789BB 0%, #67c2e6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.claim-dialog__success-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #16a34a 0%, #4ade80 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.claim-dialog__error-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

/* Title */
.claim-dialog__title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 10px;
  line-height: 1.3;
}

.claim-dialog--dark .claim-dialog__title {
  color: #e2e8f0;
}

/* Subtitle */
.claim-dialog__subtitle {
  font-size: 15px;
  color: #475569;
  margin: 0 0 24px;
  line-height: 1.5;
}

.claim-dialog--dark .claim-dialog__subtitle {
  color: #94a3b8;
}

.claim-dialog__subtitle strong {
  color: #0f172a;
}

.claim-dialog--dark .claim-dialog__subtitle strong {
  color: #e2e8f0;
}

/* OAuth buttons */
.claim-dialog__oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.claim-dialog__oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 46px;
  font-size: 15px;
  font-weight: 600;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;
  color: #334155;
}

.claim-dialog__oauth-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.claim-dialog__oauth-btn--linkedin {
  background: #0A66C2;
  border-color: #0A66C2;
  color: white;
}

.claim-dialog__oauth-btn--linkedin:hover {
  background: #004182;
  border-color: #004182;
}

.claim-dialog__oauth-btn--linkedin svg {
  fill: white;
}

.claim-dialog--dark .claim-dialog__oauth-btn {
  background: #0f172a;
  border-color: #334155;
  color: #e2e8f0;
}

.claim-dialog--dark .claim-dialog__oauth-btn:hover {
  background: #1e293b;
  border-color: #475569;
}

.claim-dialog--dark .claim-dialog__oauth-btn--linkedin {
  background: #0A66C2;
  border-color: #0A66C2;
  color: white;
}

.claim-dialog--dark .claim-dialog__oauth-btn--linkedin:hover {
  background: #004182;
}

/* Divider */
.claim-dialog__divider {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin: 20px 0;
}

.claim-dialog__divider-line {
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.claim-dialog--dark .claim-dialog__divider-line {
  background: #334155;
}

.claim-dialog__divider-text {
  font-size: 14px;
  color: #94a3b8;
  font-weight: 500;
}

/* Form */
.claim-dialog__form {
  width: 100%;
  text-align: left;
}

.claim-dialog__field {
  margin-bottom: 16px;
}

.claim-dialog__label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.claim-dialog--dark .claim-dialog__label {
  color: #cbd5e1;
}

.claim-dialog__input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  font-size: 15px;
  color: #0f172a;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.claim-dialog__input:focus {
  border-color: #3789BB;
  background: white;
}

.claim-dialog__input--dark {
  background: #0f172a;
  border-color: #334155;
  color: #e2e8f0;
}

.claim-dialog__input--dark:focus {
  border-color: #3789BB;
  background: #1e293b;
}

.claim-dialog__input--error {
  border-color: #ef4444 !important;
}

.claim-dialog__input::placeholder {
  color: #94a3b8;
}

.claim-dialog__field-error {
  display: block;
  font-size: 14px;
  color: #ef4444;
  margin-top: 4px;
}

.claim-dialog__hint {
  display: block;
  font-size: 14px;
  color: #94a3b8;
  margin-top: 4px;
}

.claim-dialog__alert {
  margin-bottom: 16px;
  width: 100%;
  font-size: 14px;
}

.claim-dialog__alert-contact {
  margin-top: 6px;
  font-size: 14px;
  opacity: 0.9;
}

.claim-dialog__alert-contact a {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
}

.claim-dialog__alert-contact a:hover {
  text-decoration: none;
}

.claim-dialog__alert-title {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 8px;
}

.claim-dialog__alert-body {
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.95;
}

.claim-dialog__alert-body a {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
}

.claim-dialog__alert-body a:hover {
  text-decoration: none;
}

.claim-dialog__alert-code {
  display: inline-block;
  padding: 1px 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  white-space: nowrap;
}

.claim-dialog--dark .claim-dialog__alert-code,
.v-theme--dark .claim-dialog .claim-dialog__alert-code {
  background: rgba(255, 255, 255, 0.12);
}

.claim-dialog__contact-line {
  margin-top: 12px !important;
  margin-bottom: 20px !important;
  font-size: 15px;
}

.claim-dialog__contact-line a {
  color: #3789BB;
  font-weight: 600;
  text-decoration: none;
}

.claim-dialog__contact-line a:hover {
  text-decoration: underline;
}

.claim-dialog--dark .claim-dialog__contact-line a,
.v-theme--dark .claim-dialog .claim-dialog__contact-line a {
  color: #67c2e6;
}

/* Email submit button */
.claim-dialog__email-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  color: #3789BB;
  background: transparent;
  border: 2px solid #3789BB;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.claim-dialog__email-btn:hover:not(:disabled) {
  background: rgba(55, 137, 187, 0.08);
}

.claim-dialog__email-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.claim-dialog__email-btn--dark {
  color: #67c2e6;
  border-color: #67c2e6;
}

.claim-dialog__email-btn--dark:hover:not(:disabled) {
  background: rgba(103, 194, 230, 0.1);
}

/* Login link */
.claim-dialog__login {
  font-size: 14px;
  color: #64748b;
  margin-top: 20px;
}

.claim-dialog--dark .claim-dialog__login {
  color: #94a3b8;
}

.claim-dialog__login-link {
  color: #3789BB;
  font-weight: 600;
  text-decoration: none;
}

.claim-dialog__login-link:hover {
  text-decoration: underline;
}

.claim-dialog--dark .claim-dialog__login-link {
  color: #67c2e6;
}

/* Success actions */
.claim-dialog__success-actions {
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
}

.claim-dialog__close-btn {
  flex: 1;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.claim-dialog__close-btn:hover {
  background: #e2e8f0;
}

.claim-dialog--dark .claim-dialog__close-btn {
  background: #334155;
  color: #cbd5e1;
}

.claim-dialog--dark .claim-dialog__close-btn:hover {
  background: #475569;
}

.claim-dialog__resend-btn {
  flex: 1;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  color: #3789BB;
  background: transparent;
  border: 2px solid #3789BB;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.claim-dialog__resend-btn:hover:not(:disabled) {
  background: rgba(55, 137, 187, 0.08);
}

.claim-dialog__resend-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.claim-dialog--dark .claim-dialog__resend-btn {
  color: #67c2e6;
  border-color: #67c2e6;
}

@media (max-width: 480px) {
  .claim-dialog {
    padding: 32px 20px 24px;
  }

  .claim-dialog__success-actions {
    flex-direction: column;
  }
}
</style>

<!-- Unscoped styles for v-dialog dark mode (teleported outside v-app) -->
<style>
/* The v-overlay wrapper gets a solid dark background from v-theme--dark.
   Make it transparent so the scrim handles the dimming instead. */
.v-dialog.claim-dialog--dark {
  background: transparent !important;
}

.v-dialog.claim-dialog--dark > .v-overlay__scrim {
  background: rgba(0, 0, 0, 0.5) !important;
  opacity: 1 !important;
}

.v-theme--dark .claim-dialog {
  background: #1e293b !important;
}

.v-theme--dark .claim-dialog .claim-dialog__title {
  color: #e2e8f0;
}

.v-theme--dark .claim-dialog .claim-dialog__subtitle {
  color: #94a3b8;
}

.v-theme--dark .claim-dialog .claim-dialog__subtitle strong {
  color: #e2e8f0;
}

.v-theme--dark .claim-dialog .claim-dialog__divider-line {
  background: #334155;
}

.v-theme--dark .claim-dialog .claim-dialog__label {
  color: #cbd5e1;
}

.v-theme--dark .claim-dialog .claim-dialog__input {
  background: #0f172a;
  border-color: #334155;
  color: #e2e8f0;
}

.v-theme--dark .claim-dialog .claim-dialog__input:focus {
  border-color: #3789BB;
  background: #1e293b;
}

.v-theme--dark .claim-dialog .claim-dialog__login {
  color: #94a3b8;
}

.v-theme--dark .claim-dialog .claim-dialog__login-link {
  color: #67c2e6;
}

.v-theme--dark .claim-dialog .claim-dialog__email-btn {
  color: #67c2e6;
  border-color: #67c2e6;
}

.v-theme--dark .claim-dialog .claim-dialog__email-btn:hover:not(:disabled) {
  background: rgba(103, 194, 230, 0.1);
}

.v-theme--dark .claim-dialog .claim-dialog__close-btn {
  background: #334155;
  color: #cbd5e1;
}

.v-theme--dark .claim-dialog .claim-dialog__close-btn:hover {
  background: #475569;
}

.v-theme--dark .claim-dialog .claim-dialog__resend-btn {
  color: #67c2e6;
  border-color: #67c2e6;
}

.v-theme--dark .claim-dialog .claim-dialog__oauth-btn {
  background: #0f172a;
  border-color: #334155;
  color: #e2e8f0;
}

.v-theme--dark .claim-dialog .claim-dialog__oauth-btn:hover {
  background: #1e293b;
  border-color: #475569;
}

.v-theme--dark .claim-dialog .claim-dialog__oauth-btn--linkedin {
  background: #0A66C2;
  border-color: #0A66C2;
  color: white;
}

.v-theme--dark .claim-dialog .claim-dialog__oauth-btn--linkedin:hover {
  background: #004182;
}
</style>
